/**
 * Utility functions for handling the new simplified translations structure
 * where translations are stored as JSON strings in a single field
 */

/**
 * Parse translations from Directus response
 * @param {Array|string|null} translationsField - The translations field from Directus (can be array of objects or JSON string)
 * @returns {Object[]} Array of translation objects
 */
export function parseTranslations(translationsField) {
  if (!translationsField) return [];

  try {
    // If it's already an array of objects (from deep query), return as is
    if (Array.isArray(translationsField)) {
      return translationsField;
    }

    // If it's a string, try to parse as JSON
    const parsed =
      typeof translationsField === "string"
        ? JSON.parse(translationsField)
        : translationsField;

    // Ensure it's an array
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch (error) {
    console.warn("Failed to parse translations:", error);
    return [];
  }
}

/**
 * Get translation for a specific language
 * @param {Array|null} translationsField - The translations field from Directus
 * @param {string} languageCode - Language code (e.g., 'en', 'sk')
 * @returns {Object|null} Translation object or null if not found
 */
export function getTranslation(translationsField, languageCode) {
  const translations = parseTranslations(translationsField);

  // Map language codes to match Directus format
  const languageMap = {
    en: "en-US",
    sk: "sk-SK",
  };

  const targetLang = languageMap[languageCode] || languageCode;

  // Find translation for the specified language
  const translation = translations.find(
    (tr) => tr.languages_code === targetLang
  );

  // Fallback to first translation if language not found
  return translation || translations[0] || null;
}

/**
 * Get available languages from translations
 * @param {Array|null} translationsField - The translations field from Directus
 * @returns {string[]} Array of language codes
 */
export function getAvailableLanguages(translationsField) {
  const translations = parseTranslations(translationsField);

  // Extract language codes from translations
  const languageCodes = translations
    .map((tr) => tr.languages_code)
    .filter(Boolean);

  // Map Directus language codes to our app codes
  const languageMap = {
    "en-US": "en-US",
    "sk-SK": "sk-SK",
    "de-DE": "de-DE",
    "ru-RU": "ru-RU",
    "ua-UA": "ua-UA",
  };

  return languageCodes
    .map((code) => languageMap[code] || code)
    .filter((code, index, arr) => arr.indexOf(code) === index); // Remove duplicates
}

/**
 * Get title from translation in the specified language
 * @param {string|null} translationsField - The translations field from Directus
 * @param {string} languageCode - Language code (e.g., 'en', 'sk')
 * @param {string} fallback - Fallback title if not found
 * @returns {string} Title or fallback
 */
export function getTranslatedTitle(
  translationsField,
  languageCode,
  fallback = "Untitled"
) {
  const translation = getTranslation(translationsField, languageCode);
  return translation?.title || fallback;
}

/**
 * Get content from translation in the specified language
 * @param {string|null} translationsField - The translations field from Directus
 * @param {string} languageCode - Language code (e.g., 'en', 'sk')
 * @param {string} fallback - Fallback content if not found
 * @returns {string} Content or fallback
 */
export function getTranslatedContent(
  translationsField,
  languageCode,
  fallback = ""
) {
  const translation = getTranslation(translationsField, languageCode);
  return translation?.content || fallback;
}

/**
 * Get excerpt from translation in the specified language
 * @param {string|null} translationsField - The translations field from Directus
 * @param {string} languageCode - Language code (e.g., 'en', 'sk')
 * @param {string} fallback - Fallback excerpt if not found
 * @returns {string} Excerpt or fallback
 */
export function getTranslatedExcerpt(
  translationsField,
  languageCode,
  fallback = ""
) {
  const translation = getTranslation(translationsField, languageCode);
  // Since translations don't have excerpt field, return fallback
  return fallback;
}

/**
 * Get country names from countries field
 * @param {string|null} countriesField - The countries field from Directus (JSON string or simple string)
 * @returns {string[]} Array of country names
 */
export function getCountryNames(countriesField) {
  if (!countriesField) return [];

  try {
    // If it's a JSON string, parse it
    if (typeof countriesField === "string") {
      const parsed = JSON.parse(countriesField);
      if (Array.isArray(parsed)) {
        return parsed
          .map((country) => country?.name || country)
          .filter(Boolean);
      }
      // If it's a single country object
      if (parsed?.name) {
        return [parsed.name];
      }
      // If it's just a string, return as is
      return [countriesField];
    }

    // If it's already an array
    if (Array.isArray(countriesField)) {
      return countriesField
        .map((country) => country?.name || country)
        .filter(Boolean);
    }

    return [];
  } catch (error) {
    // If parsing fails, treat as simple string
    return [countriesField];
  }
}

/**
 * Get first country name or fallback
 * @param {string|null} countriesField - The countries field from Directus
 * @param {string} fallback - Fallback text if no countries
 * @returns {string} Country name or fallback
 */
export function getFirstCountryName(
  countriesField,
  fallback = "—",
  languageCode = "en"
) {
  if (!countriesField) return fallback;

  try {
    // If it's an array of country objects with nested Countries_id structure
    if (Array.isArray(countriesField)) {
      const firstCountry = countriesField[0];
      if (firstCountry?.Countries_id?.translations) {
        // Try to get translated name
        const translation = getTranslation(
          firstCountry.Countries_id.translations,
          languageCode
        );
        if (translation?.name) {
          return translation.name;
        }
      }
      // Fallback to key if no translation found
      if (firstCountry?.Countries_id?.key) {
        return firstCountry.Countries_id.key;
      }
      return fallback;
    }

    // If it's a JSON string, parse it
    if (typeof countriesField === "string") {
      const parsed = JSON.parse(countriesField);
      if (Array.isArray(parsed)) {
        const firstCountry = parsed[0];
        if (firstCountry?.Countries_id?.translations) {
          const translation = getTranslation(
            firstCountry.Countries_id.translations,
            languageCode
          );
          if (translation?.name) {
            return translation.name;
          }
        }
        return firstCountry?.Countries_id?.key || fallback;
      }
      return parsed?.Countries_id?.key || fallback;
    }

    return fallback;
  } catch (error) {
    return fallback;
  }
}
